/**
 * Hook RTEmpresa — responsáveis técnicos vinculados a empresas.
 *
 * Endpoint pós-refator atores-e-automacao: /api/v1/rts-empresa/
 * Substitui o uso de Usuario.perfil='rt_*' como FK de RT em PMOC.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import type { PaginatedResponse, RTEmpresa } from '../types'

export interface RTEmpresaListParams {
  empresa?: string
  search?: string
  ordering?: string
  page?: number
  page_size?: number
}

export function useRTsEmpresaList(params?: RTEmpresaListParams) {
  return useQuery({
    queryKey: ['rts-empresa', params],
    queryFn: async () => {
      const r = await api.get<PaginatedResponse<RTEmpresa>>('/rts-empresa/', { params })
      return r.data
    },
    staleTime: 1000 * 60 * 2,
  })
}

export function useRTEmpresaDetalhe(id: string) {
  return useQuery({
    queryKey: ['rts-empresa', id],
    queryFn: async () => {
      const r = await api.get<RTEmpresa>(`/rts-empresa/${id}/`)
      return r.data
    },
    enabled: !!id,
  })
}

export interface RTEmpresaCreateInput {
  empresa: string
  nome: string
  titulo_profissional?: string
  conselho: RTEmpresa['conselho']
  conselho_outro_descricao?: string
  numero_registro: string
  uf: string
  rnp_confea?: string
  email: string
  is_principal?: boolean
}

export function useRTEmpresaCreate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: RTEmpresaCreateInput) => {
      const response = await api.post<RTEmpresa>('/rts-empresa/', data)
      return response.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rts-empresa'] })
    },
  })
}

export function useRTEmpresaReenviarAceite() {
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post<{ detail: string; email: string; expira_em: string }>(
        `/rts-empresa/${id}/reenviar-aceite/`,
      )
      return response.data
    },
  })
}

export function useRTEmpresaUpdate(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<RTEmpresaCreateInput>) =>
      api.patch<RTEmpresa>(`/rts-empresa/${id}/`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rts-empresa'] })
      qc.invalidateQueries({ queryKey: ['rts-empresa', id] })
    },
  })
}
